/**
 * Une liste d'activités filtrée — par commune, ou par période de vacances.
 *
 * > « Créer automatiquement des landing pages indexables : /stages/ixelles,
 * >   /stages/schaerbeek… Créer également des pages du type /stages/ete-2026. »
 *
 * Une seule vue pour les deux, parce que ce sont les mêmes pages à
 * l'introduction près : un titre, une phrase qui situe, la liste des activités
 * rangées par type, et des liens vers les pages voisines.
 *
 * ## Pourquoi `/stages/commune/ixelles` et non `/stages/ixelles`
 *
 * Le cahier des charges propose la seconde forme, plus courte. Mais
 * `/stages/:slug` sert déjà les pages de stage, et rien n'interdit à un slug de
 * stage de valoir « ixelles » — le routeur choisirait alors une page au hasard
 * entre deux sens. Un segment qui dit ce qu'il désigne coûte huit caractères et
 * supprime la classe d'erreur ; les moteurs ne pénalisent pas un niveau de plus.
 *
 * Comme `StageVue` et `CatalogueVue`, cette vue ne lit rien : elle est rendue
 * telle quelle au build par `renderToString`.
 */

import { Link } from 'react-router-dom';
import { ORGANISATEUR, type StagePublic } from '../../lib/seo/stage';
import { activitesParType, type Regroupement } from '../../lib/seo/catalogue';
import CarteDActivite from './CarteDActivite';

interface Props {
  /** Le titre de la page, en toutes lettres. */
  titre: string;
  /** Une ou deux phrases qui situent ce que la page regroupe. */
  introduction: string;
  stages: StagePublic[];
  /** Le fil d'Ariane, sans l'élément courant. */
  filDAriane: { nom: string; chemin: string }[];
  /** Les autres pages du même genre — le maillage horizontal. */
  voisines: { titre: string; liens: Regroupement[]; base: string };
}

const ListeVue = ({ titre, introduction, stages, filDAriane, voisines }: Props) => (
  <div className="max-w-5xl mx-auto px-4 py-8">
    <nav aria-label="Fil d’Ariane" className="text-sm text-gray-500 mb-5">
      <ol className="flex flex-wrap items-center gap-1">
        {filDAriane.map((e) => (
          <li key={e.chemin} className="flex items-center gap-1">
            <Link to={e.chemin} className="hover:text-primary-700">
              {e.nom}
            </Link>
            <span aria-hidden="true">›</span>
          </li>
        ))}
        <li className="text-gray-700">{titre}</li>
      </ol>
    </nav>

    <h1 className="text-3xl font-bold text-gray-900 mb-3">{titre}</h1>
    <p className="text-gray-700 mb-8">{introduction}</p>

    {/*
      Les mêmes cartes que le catalogue, pour la même raison : une commune
      porte jusqu'à dix séances d'une poignée d'activités, et les lister une par
      une redonnait dix fois le même titre. Chaque séance garde son lien.
    */}
    {stages.length === 0 ? (
      <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center mb-10">
        <p className="text-gray-700">
          Aucune activité n’est ouverte aux inscriptions ici pour le moment.{' '}
          {ORGANISATEUR} en organise toute l’année —{' '}
          <Link to="/stages" className="text-primary-700 underline">
            voir tout le catalogue
          </Link>
          .
        </p>
      </div>
    ) : (
      <div className="space-y-10 mb-12">
        {activitesParType(stages).map((section) => (
          <section key={section.type}>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.titre}</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {section.activites.map((a) => (
                <CarteDActivite key={a.nom} activite={a} />
              ))}
            </div>
          </section>
        ))}
      </div>
    )}

    {/*
      Le maillage horizontal : d'une commune vers les autres communes, d'une
      période vers les autres périodes. C'est ce qui permet à un robot de
      parcourir tout le catalogue sans dépendre du sitemap (§18).
    */}
    {voisines.liens.length > 0 && (
      <section className="border-t border-gray-200 pt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{voisines.titre}</h2>
        <ul className="flex flex-wrap gap-x-4 gap-y-2">
          {voisines.liens.map((v) => (
            <li key={v.slug}>
              <Link
                to={`${voisines.base}/${v.slug}`}
                className="text-primary-700 hover:text-primary-900 hover:underline"
              >
                {v.nom}
                <span className="text-gray-500"> ({v.stages.length})</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    )}
  </div>
);

export default ListeVue;
